/**
 * ExpenseCategoryChart - 费用类别统计图表组件
 * 
 * 显示费用按类别的分布情况，带有环形图表和类别列表
 */
import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ViewStyle,
  Dimensions,
  TouchableOpacity,
  Modal,
  Pressable,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { MODULE_COLORS } from '@cubemeet/core';

export interface CategoryStat {
  /**
   * 类别名称（已翻译）
   */
  category: string;
  /**
   * 类别金额
   */
  amount: number;
  /**
   * 占比百分比
   */
  percentage: number;
  /**
   * 类别颜色
   */
  color: string;
  /**
   * 类别图标名称（可选）
   */
  icon?: string;
}

export interface ExpenseCategoryChartProps {
  /**
   * 类别统计数据
   */
  categories: CategoryStat[];
  /**
   * 货币代码
   */
  currency?: string;
  /**
   * 格式化货币显示
   */
  formatCurrency: (amount: number, currency?: string) => string;
  /**
   * 紧凑格式化（可选），用于环形图中间与列表中的紧凑显示
   */
  formatCurrencyCompact?: (amount: number, currency?: string) => string;
  /**
   * 紧凑阈值，超过该值点击会弹出完整金额（默认 10000）
   */
  compactThreshold?: number;
  /**
   * 点击金额后的自定义回调（优先于内置模态）
   */
  onAmountPress?: (fullAmount: string) => void;
  /**
   * 翻译函数
   */
  t?: (key: string) => string;
  /**
   * 自定义样式
   */
  style?: ViewStyle;
  /**
   * 最多显示的类别数（默认5）
   */
  maxCategories?: number;
  /** 可选的头部操作区（例如 PDF / Upload 按钮） */
  headerActions?: React.ReactNode;
  /**
   * Optional custom icon renderer for web: (name, size, color) => ReactNode
   */
  renderIcon?: (name: string | undefined, size: number, color: string) => React.ReactNode;
}

const defaultT = (key: string) => {
  const translations: Record<string, string> = {
    'categoryChart.title': '类别统计',
    'expenseManagement.categoryChart.title': '类别统计',
    'categoryChart.empty': '暂无类别统计数据',
    'categoryChart.other': '其他',
  };
  return translations[key] || key;
};

const isLikelyKey = (s: any) => typeof s === 'string' && (s.indexOf('.') !== -1 || s.indexOf('expenseManagement') === 0);

const resolveT = (tFunc: ((k: string) => string) | undefined, key: string) => {
  try {
    if (!tFunc) return defaultT(key);
    // try raw key first
    let res = tFunc(key);
    if (!res || isLikelyKey(res)) {
      // try prefixed key
      res = tFunc(`expenseManagement.${key}`);
      if (!res || isLikelyKey(res)) return defaultT(key);
    }
    return res;
  } catch (e) {
    return defaultT(key);
  }
};

/**
 * ExpenseCategoryChart 组件
 */
const ExpenseCategoryChart: React.FC<ExpenseCategoryChartProps> = ({
  categories,
  currency,
  formatCurrency,
  formatCurrencyCompact,
  compactThreshold = 10000,
  onAmountPress,
  t = defaultT,
  style,
  maxCategories = 5,
  headerActions,
  renderIcon,
}) => {
  // 空状态
  if (categories.length === 0) {
    return (
      <View style={[styles.container, style]}>
          <Text style={styles.title}>{resolveT(t, 'categoryChart.title')}</Text>
        <View style={styles.emptyState}>
          {renderIcon ? renderIcon('donut_large', 42, '#cbd5e1') : <Icon name="donut_large" size={42} color="#cbd5e1" />}
            <Text style={styles.emptyText}>{resolveT(t, 'categoryChart.empty')}</Text>
        </View>
      </View>
    );
  }

  // 按金额排序并限制显示数量
  const sortedCategories = [...categories].sort((a, b) => b.amount - a.amount);
  const displayCategories = sortedCategories.slice(0, maxCategories);
  
  // 如果有超过maxCategories的类别，合并为"其他"
  const otherCategories = sortedCategories.slice(maxCategories);
  if (otherCategories.length > 0) {
    const otherTotal = otherCategories.reduce((sum, cat) => sum + cat.amount, 0);
    const otherPercentage = otherCategories.reduce((sum, cat) => sum + cat.percentage, 0);
    displayCategories.push({
      category: resolveT(t, 'categoryChart.other'),
      amount: otherTotal,
      percentage: otherPercentage,
      color: '#94a3b8',
    });
  }

  // 计算总金额
  const totalAmount = displayCategories.reduce((sum, cat) => sum + cat.amount, 0);

  // 创建环形图的数据
  const createRingData = () => {
    let currentAngle = 0;
    return displayCategories.map((cat) => {
      const angle = (cat.percentage / 100) * 360;
      const data = {
        ...cat,
        startAngle: currentAngle,
        endAngle: currentAngle + angle,
        angle,
      };
      currentAngle += angle;
      return data;
    });
  };

  const ringData = createRingData();
  // Render concentric rings: compute largest radius based on number of categories
  const baseInnerRadius = 80; // innermost ring radius
  const ringSpacing = 14; // spacing between ring centers
  const strokeWidth = 12; // ring thickness
  const largestRadius = baseInnerRadius + Math.max(0, displayCategories.length - 1) * ringSpacing;
  const center = largestRadius + strokeWidth; // center coordinate
  const circumference = (r: number) => 2 * Math.PI * r;

  const [modalVisible, setModalVisible] = useState(false);
  const [modalAmount, setModalAmount] = useState<number | null>(null);

  const handleAmountPress = (amount: number) => {
    if (onAmountPress) {
      onAmountPress(formatCurrency(amount, currency));
      return;
    }
    if (Math.abs(amount) >= compactThreshold) {
      setModalAmount(amount);
      setModalVisible(true);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setModalAmount(null);
  };

  const modalFullLabel = useMemo(() => {
    if (modalAmount == null) return '';
    return formatCurrency(modalAmount, currency);
  }, [modalAmount, formatCurrency, currency]);

  return (
    <View style={[styles.container, style]}>
          <View style={styles.headerRow}>
            <View style={styles.headerLeft}>
              <View style={styles.headerIcon}>
                {renderIcon ? renderIcon('donut_large', 18, MODULE_COLORS.travel) : <Icon name="donut_large" size={18} color={MODULE_COLORS.travel} />}
              </View>
              <Text style={styles.title}>{resolveT(t, 'categoryChart.title')}</Text>
            </View>
            {headerActions ? <View style={styles.headerActions}>{headerActions}</View> : null}
          </View>

      {/* 环形图表 */}
      <View style={styles.chartContainer}>
        <View style={[styles.ringChart, { width: center * 2, height: center * 2 }]}>
          {/* 背景圆环 (largest) */}
          <View style={[styles.ring, styles.backgroundRing, {
            width: largestRadius * 2,
            height: largestRadius * 2,
            borderRadius: largestRadius,
            borderWidth: strokeWidth,
          }]} />

          {/* 各类别的圆环段（同心，半径递减） */}
          {ringData.map((cat, index) => {
            const ringRadius = largestRadius - index * ringSpacing;
            // strokeDash calculations kept for potential svg usage; here we simulate with rotated bordered views
            return (
              <View
                key={`ring-${index}`}
                style={[
                  styles.ring,
                  {
                    width: ringRadius * 2,
                    height: ringRadius * 2,
                    borderRadius: ringRadius,
                    borderWidth: strokeWidth,
                    borderColor: cat.color,
                    transform: [{ rotate: `${cat.startAngle - 90}deg` }],
                  },
                ]}
              />
            );
          })}
          
          {/* 中心内容 */}
          <View style={styles.centerContent}>
            <TouchableOpacity activeOpacity={0.8} onPress={() => handleAmountPress(totalAmount)}>
              <Text style={styles.totalAmount} numberOfLines={1} ellipsizeMode="tail">
                {(formatCurrencyCompact ? formatCurrencyCompact(totalAmount, currency) : formatCurrency(totalAmount, currency))}
              </Text>
            </TouchableOpacity>
            <Text style={styles.totalLabel}>Total</Text>
          </View>
        </View>

        {/* Icons around ring removed - icons will be shown in the list below */}
      </View>

      {/* 类别列表 */}
      <View style={styles.categoryList}>
        {displayCategories.map((cat, index) => (
          <View key={`${cat.category}-${index}`} style={styles.categoryItem}>
            <View style={styles.categoryInfo}>
              <View style={[styles.categoryIconList, { backgroundColor: (cat.color || '#95a5a6') + '15', borderColor: cat.color || '#95a5a6' }]}>
                <Icon name={(cat.icon as any) || 'category'} size={16} color={cat.color || '#95a5a6'} />
              </View>
              <Text style={styles.categoryName}>{cat.category}</Text>
            </View>
            <View style={styles.categoryValues}>
              <TouchableOpacity activeOpacity={0.8} onPress={() => handleAmountPress(cat.amount)}>
                <Text style={styles.categoryAmount} numberOfLines={1} ellipsizeMode="tail">
                  {(formatCurrencyCompact ? formatCurrencyCompact(cat.amount, currency) : formatCurrency(cat.amount, currency))}
                </Text>
              </TouchableOpacity>
              <Text style={styles.categoryPercentage}>{cat.percentage.toFixed(1)}%</Text>
            </View>
          </View>
        ))}
      </View>

      {/* 内置模态 */}
      <Modal visible={modalVisible} animationType="fade" transparent onRequestClose={closeModal}>
        <Pressable style={styles.modalOverlay} onPress={closeModal}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Full amount</Text>
            <Text style={styles.modalAmount}>{modalFullLabel}</Text>
            <TouchableOpacity style={styles.modalClose} onPress={closeModal}>
              <Text style={styles.modalCloseText}>Close</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    ...Platform.select({
      web: {
        boxShadow: '0px 2px 8px rgba(0,0,0,0.05)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
      },
    }),
    borderWidth: 1,
    borderColor: '#f1f5f9',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerIcon: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    borderRadius: 8,
    backgroundColor: 'transparent',
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
  },
  chartContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    position: 'relative',
    height: 240,
  },
  ringChart: {
    width: 200,
    height: 200,
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ring: {
    position: 'absolute',
    width: 160,
    height: 160,
    borderRadius: 80,
    borderWidth: 20,
  },
  backgroundRing: {
    borderColor: '#f1f5f9',
  },
  centerContent: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  totalAmount: {
    fontSize: 24,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  totalLabel: {
    fontSize: 14,
    color: '#6b7280',
    fontWeight: '500',
  },
  categoryIconRing: {
    position: 'absolute',
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
  },
  categoryList: {
    marginTop: 8,
  },
  categoryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  categoryInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  categoryDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  categoryIconList: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    borderWidth: 2,
  },
  categoryName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#374151',
    flex: 1,
  },
  categoryValues: {
    alignItems: 'flex-end',
  },
  categoryAmount: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 2,
  },
  categoryPercentage: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyText: {
    fontSize: 14,
    color: '#9ca3af',
    marginTop: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalContent: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 8,
  },
  modalAmount: {
    fontSize: 22,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 16,
  },
  modalClose: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#f1f5f9',
  },
  modalCloseText: {
    fontSize: 14,
    color: MODULE_COLORS.travel,
    fontWeight: '600',
  },
});

export default ExpenseCategoryChart;
